import { useTranslation } from "hhn-commons";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import { useUnresolvedSendWarning } from "../hooks/useUnresolvedSendWarning";
import { UnsentMessagesGuardContext } from "./UnsentMessagesGuardContext";

interface UnsentMessagesGuardProviderProps {
  children: React.ReactNode;
}

/**
 * Tells the user about messages that have not been sent before they navigate
 * away from the chat holding them. Nothing is lost by leaving — the send keeps
 * resolving and the outbox keeps the record — so this exists to inform, not to
 * protect, and leaving is always allowed.
 *
 * Clicks are caught on the document rather than wired into each link, so this
 * covers switching chat, switching organisation and every other route without
 * every navigation site having to remember to opt in.
 */
export const UnsentMessagesGuardProvider = ({
  children,
}: UnsentMessagesGuardProviderProps) => {
  const { t } = useTranslation();
  const tNs = (key: string) => t(key, { ns: "message" });
  const navigate = useNavigate();
  const [hasUnsentMessages, setHasUnsentMessages] = useState(false);
  const [blockedDestination, setBlockedDestination] = useState<string | null>(
    null,
  );

  useUnresolvedSendWarning(hasUnsentMessages);

  useEffect(() => {
    if (!hasUnsentMessages) {
      return;
    }

    const interceptNavigation = (event: MouseEvent) => {
      // Leave anything that is not a plain left-click alone, so opening a chat
      // in a new tab still works and never loses the current one.
      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.defaultPrevented
      ) {
        return;
      }

      const link = (event.target as HTMLElement | null)?.closest?.("a[href]");
      if (!(link instanceof HTMLAnchorElement)) {
        return;
      }
      if (link.target === "_blank" || link.hasAttribute("download")) {
        return;
      }
      if (link.origin !== window.location.origin) {
        return;
      }
      const destination = link.pathname + link.search + link.hash;
      if (destination === window.location.pathname + window.location.search) {
        return;
      }

      // Capture phase, so the link's own handlers do not run either — the
      // navigation is being cancelled, not merely redirected.
      event.preventDefault();
      event.stopPropagation();
      setBlockedDestination(destination);
    };

    document.addEventListener("click", interceptNavigation, true);
    return () =>
      document.removeEventListener("click", interceptNavigation, true);
  }, [hasUnsentMessages]);

  return (
    <UnsentMessagesGuardContext.Provider value={{ setHasUnsentMessages }}>
      {children}
      <ConfirmationDialog
        isOpen={blockedDestination !== null}
        onClose={() => setBlockedDestination(null)}
        confirmationTitle={tNs("unsentMessages.title")}
        confirmationMessage={tNs("unsentMessages.description")}
        confirmationButtonText={tNs("unsentMessages.leave")}
        confirmationCancelText={tNs("unsentMessages.stay")}
        onConfirmationAction={() => {
          const destination = blockedDestination;
          setBlockedDestination(null);
          if (destination) {
            navigate(destination);
          }
        }}
      />
    </UnsentMessagesGuardContext.Provider>
  );
};
